import { forwardRef, useState, useRef, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  IconButton,
  Tooltip,
  Typography,
  Grid,
  CircularProgress,
  Box,
  ButtonBase,
  Paper,
  Button,
  TextField,
  InputBase,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Field, FieldProps, getIn, useFormikContext } from 'formik';
import { useBreakpointQuery } from '../../../../utils';
import { JItem } from '../../jformik/jformik.types';
import { JItemMapper } from '../../jformik/JItemMapper';
import { useJFormikContext } from '../../jformik/jformik.context';
import EditorToolbar, { modules, formats } from './Toolbar';

const FField = ({
  jItem,
  values,
  remove,
}: {
  jItem: JItem;
  values: any;
  remove: Function;
}) => {
  const { gridProps } = jItem;
  const gridSizesResult =
    typeof gridProps === 'function'
      ? gridProps(values)
      : gridProps !== null
      ? gridProps
      : {};
  const gridSizes = typeof gridSizesResult === 'object' ? gridSizesResult : {};
  return (
    <Paper
      sx={{
        px: { xs: 2, sm: 3, md: 5 },
        pt: 5,
        pb: 3,
        my: 1,
        // mb: 5,
        bgColor: 'white',
        position: 'relative',
      }}
    >
      <Grid item xs={12} {...gridSizes} key={`${name}-index`}>
        <JItemMapper jItem={jItem} />
      </Grid>
      <Paper
        elevation={5}
        sx={{
          position: 'absolute',
          top: { xs: -10, sm: -15 },
          right: { xs: -10, sm: -15 },
          bgcolor: theme => theme.palette.secondary.main,
          borderRadius: '100%',
        }}
      >
        <IconButton
          onClick={() => {
            remove();
          }}
          sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
          }}
        >
          <Delete
            sx={{
              color: theme => theme.palette.common.white,
              fontSize: { xs: 18, sm: 24 },
            }}
          />
        </IconButton>
      </Paper>
    </Paper>
  );
};

/*
 * Custom "star" icon for the toolbar using an Octicon
 * https://octicons.github.io
 */
const CustomHeart = () => <span>♥</span>;

/*
 * Custom toolbar component including insertStar button and dropdowns
 */
const CustomToolbar = () => (
  <div id="toolbar">
    <select className="ql-header" defaultValue={''} onChange={e => e.persist()}>
      <option value="1" />
      <option value="2" />
      <option selected />
    </select>
    <button className="ql-bold" />
    <button className="ql-italic" />
    <select className="ql-color">
      <option value="red" />
      <option value="green" />
      <option value="blue" />
      <option value="orange" />
      <option value="violet" />
      <option value="#d0d1d2" />
      <option selected />
    </select>
    <button className="ql-insertStar">
      <CustomHeart />
    </button>
  </div>
);

function insertHeart() {
  // const cursorPosition = this.quill.getSelection().index;
  // this.quill.insertText(cursorPosition, '♥');
  // this.quill.setSelection(cursorPosition + 1);
}

export const FQuill = forwardRef((jItem: JItem, ref) => {
  const itemsRef = useRef({});
  const quillRef: any = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeText, setActiveText] = useState<{
    before?: string;
    cursor?: number;
    ref?: any;
    index: number;
    after?: string;
  }>({ index: 0 });

  const smDown = useBreakpointQuery('sm-down');
  const { disabled: fDisabled = false } = useJFormikContext();
  const disabled = fDisabled || jItem.disabled;
  /*
   * Event handler to be attached using Quill toolbar module (see line 73)
   * https://quilljs.com/docs/modules/toolbar/
   */
  // function insertHeart() {
  //   // if (quillRef?.current) {
  //   //   const cursorPosition = (quillRef?.current as any).getSelection().index;
  //   //   (quillRef?.current as any).insertText(cursorPosition, '♥');
  //   //   (quillRef?.current as any).setSelection(cursorPosition + 1);
  //   // }
  // }

  /*
   * Quill modules to attach to editor
   * See https://quilljs.com/docs/modules/ for complete options
   */
  // const modules = {
  //   toolbar: {
  //     container: '#toolbar',
  //     handlers: {
  //       // handlers object will be merged with default handlers object
  //       link: function (value: any) {
  //         if (value) {
  //           var href = prompt('Enter the URL');
  //           (this as any).quill.format('link', href);
  //         } else {
  //           (this as any).quill.format('link', false);
  //         }
  //       },
  //     },
  //   },
  //   history: {
  //     delay: 500,
  //     maxStack: 100,
  //     userOnly: true,
  //   },
  // };
  // /*
  //  * Quill editor formats
  //  * See https://quilljs.com/docs/formats/
  //  */
  // const formats = [
  //   'header',
  //   'font',
  //   'size',
  //   'bold',
  //   'italic',
  //   'underline',
  //   'strike',
  //   'blockquote',
  //   'list',
  //   'bullet',
  //   'indent',
  //   'link',
  //   'image',
  //   'color',
  // ];
  // Add sizes to whitelist and register them
  const Size = Quill.import('formats/size');
  Size.whitelist = ['extra-small', 'small', 'medium', 'large'];
  Quill.register(Size, true);

  // Add fonts to whitelist and register them
  const Font = Quill.import('formats/font');
  Font.whitelist = [
    'arial',
    'comic-sans',
    'courier-new',
    'georgia',
    'helvetica',
    'lucida',
  ];
  Quill.register(Font, true);
  const [state, setState] = useState({ editorHtml: '' });

  const handleChange = (html: any) => {
    setState({ editorHtml: html });
  };
  return (
    <Field name={jItem.name}>
      {({
        field: { name, value, onChange, onBlur },
        form: { touched, values, errors, submitCount, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
        meta,
      }: FieldProps) => {
        return (
          <div className="text-editor">
            <EditorToolbar />
            <ReactQuill
              ref={quillRef}
              theme={'snow'}
              preserveWhitespace={true}
              value={value}
              onChange={onChange(name)}
              // value={state.editorHtml}
              // onChange={handleChange}
              placeholder={jItem.jItemProps?.placeholder || 'sfd'}
              modules={modules}
              formats={formats}
            />
          </div>
        );
      }}
    </Field>
  );
});
